import { generateURL } from '@Globals/Algolia/Fetcher'
import { AlgoliaProductV2 } from '@Globals/Algolia/Types'

interface AlgoliaHighlightResult {
  fullyHighlighted: boolean
  matchLevel: 'full'
  matchedWords: string[]
  value: string
}

interface AlgoliaCommon {
  _highlightResult: Record<string, AlgoliaHighlightResult>
  objectID: string
}

export interface AlgoliaCategory extends AlgoliaCommon {
  depth: number
  parentTitle: string
  slug: string
  title: string
}

export type AlgoliaProduct = AlgoliaProductV2 & AlgoliaCommon

export interface SearchResults {
  categories: AlgoliaCategory[]
  products: AlgoliaProduct[]
}

// Algolia is caching requests (not responses), so multiple calls to search with the same parameters
// will not result in excessive network requests to algolia.
//
// intentFilters are the dictionary boosts; an empty array just runs the search unboosted.
export const search = async (locale: string, query: string, intentFilters: string[] = []): Promise<SearchResults> => {
  if (!query) {
    return { categories: [], products: [] }
  }

  const algoliaLocale = locale.toLowerCase().replace('-', '_')

  const productParams = new URLSearchParams({
    // groupProducts is heavy and unused in search-as-you-type
    attributesToRetrieve: JSON.stringify(['*', '-groupProducts']),
    hitsPerPage: window.innerHeight > 768 ? '8' : '6',
    optionalWords: query,
  })
  if (intentFilters.length > 0) {
    productParams.set('optionalFilters', JSON.stringify(intentFilters))
  }

  const res = await fetch(generateURL(), {
    method: 'POST',
    headers: new Headers({ 'Content-Type': 'application/json; charset=UTF-8' }),
    body: JSON.stringify({
      requests: [
        {
          indexName: `categories_${algoliaLocale}`,
          params: new URLSearchParams({ hitsPerPage: '11', optionalWords: query }).toString(),
          query,
        },
        {
          indexName: `products_${algoliaLocale}`,
          params: productParams.toString(),
          query,
        },
      ],
    }),
  })

  // Throw rather than resolve empty, so the dropdown keeps its last suggestions instead of blanking mid-typing.
  if (!res.ok) {
    throw new Error('search request failed')
  }

  const response = await res.json()
  if (!response?.results) {
    throw new Error('search request failed')
  }

  const [
    { hits: categoryItems },
    { hits: productItems },
  ] = response.results

  return {
    categories: <AlgoliaCategory[]>categoryItems,
    products: <AlgoliaProduct[]>productItems,
  }
}
